import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@backstage/core-plugin-api';
import { customAlertApiRef } from '@agilelab/plugin-wb-platform';
import {
  GenerateAccessTokenRequest,
  GenerateAccessTokenResponse,
} from '@agilelab/plugin-wb-service-accounts-common';
import { identityApiRef } from '@backstage/core-plugin-api';
import { QUERY_KEY_ACCESS_TOKENS } from '../constants';
import { accessTokenApiRef } from '../plugin';
import { AccessToken } from '@agilelab/plugin-wb-auth-backend';

export interface CreateAccessTokenRequest {
  accountRef: string;
  expirationDate?: string;
}

export const useCreateAccessToken = (options?: { alertOnError?: boolean }) => {
  const queryClient = useQueryClient();
  const alertApi = useApi(customAlertApiRef);
  const identityApi = useApi(identityApiRef);
  const accessTokenApi = useApi(accessTokenApiRef);

  const createAccessToken = async (
    request: CreateAccessTokenRequest,
  ): Promise<AccessToken> => {
    const identityRef = await identityApi.getBackstageIdentity();
    const userEntityRef = identityRef.userEntityRef;

    const apiRequest: GenerateAccessTokenRequest = {
      account_ref: request.accountRef,
      expires_at: request.expirationDate || null,
      created_by: userEntityRef,
      updated_by: userEntityRef,
    };

    const response: GenerateAccessTokenResponse =
      await accessTokenApi.generateAccessToken(apiRequest);

    return response;
  };

  const defaultOnSuccess = (_newToken: AccessToken) => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_ACCESS_TOKENS],
    });
  };

  return useMutation({
    mutationFn: createAccessToken,
    onSuccess: defaultOnSuccess,
    onError: (error: Error) => {
      if (options?.alertOnError === false) return;
      alertApi.post({
        severity: 'error',
        message: error.message || 'Failed to create access token',
      });
    },
  });
};
